import * as THREE from 'three';
import { SpriteNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `SpriteNodeMaterial` asset: billboard sprites with authored tint color. */
@EngineClass('SpriteNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Sprite Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class SpriteNodeMaterialAsset extends NodeMaterialAsset(SpriteNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Sprite color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0, max: Math.PI * 2, step: 0.01, description: 'Sprite rotation in radians' })
  override rotation = 0;

  @property({ type: 'boolean', description: 'Use size attenuation with camera distance' })
  override sizeAttenuation = true;

  @property({ type: 'texturePath', description: 'Sprite map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Sprite alpha map path' })
    alphaMapPath: TexturePath = '';

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.rotation = THREE.MathUtils.euclideanModulo(
      this.clampFiniteNumber(this.rotation, -Math.PI * 2, Math.PI * 2, 0),
      Math.PI * 2
    );
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace, THREE.ClampToEdgeWrapping, THREE.ClampToEdgeWrapping);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace, THREE.ClampToEdgeWrapping, THREE.ClampToEdgeWrapping);
    this.colorNode = null;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): SpriteNodeMaterialAsset {
    const instance = new SpriteNodeMaterialAsset();
    SpriteNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
